import { HttpInterceptorFn, HttpErrorResponse, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../Provider/auth.service';
import { isExpiredCalendarConnectionError } from '../utils/google-calendar-consent.utils';
import { isPublicLeaderboardApiUrl } from '../utils/public-leaderboard.utils';

const AUTH_RETRY_HEADER = 'X-Auth-Retry';

function retryWithAccessToken(req: HttpRequest<unknown>, next: HttpHandlerFn, token: string) {
  return next(
    req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        [AUTH_RETRY_HEADER]: '1'
      },
      withCredentials: true
    })
  );
}

/** On 401: refresh via shared in-flight request, then retry with new JWT. Register after errorInterceptor. */
export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const shouldHandle401 =
        error.status === 401 &&
        !req.headers.has(AUTH_RETRY_HEADER) &&
        !req.url.toLowerCase().includes('/auth/') &&
        !isPublicLeaderboardApiUrl(req.url) &&
        !isExpiredCalendarConnectionError(error);
      if (!shouldHandle401) return throwError(() => error);

      return auth.refreshAccessToken().pipe(
        switchMap((newToken) => retryWithAccessToken(req, next, newToken)),
        catchError((err) => {
          const recoveredToken = auth.getValidAccessToken();
          if (recoveredToken) return retryWithAccessToken(req, next, recoveredToken);

          auth.resolveRefreshFailure(err);
          return throwError(() => err);
        })
      );
    })
  );
};
